import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

/* ============================================================
   TYPES
============================================================ */

export type TrabajadorHorario = {
  id_usuario: string;
  nombre_completo: string;
  email: string;
  foto: string | null;
  tiene_horario: boolean;
  seleccionado: boolean;
};

/* ============================================================
   HOOK
============================================================ */

export function useAplicarHorarioEmpresa(
  idEmpresa: string,
  idTemporada: string | null = null
) {
  const [trabajadores, setTrabajadores] = useState<TrabajadorHorario[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const filtrarPorTemporada = (query: any) =>
    idTemporada
      ? query.eq('id_horario_temporada', idTemporada)
      : query.is('id_horario_temporada', null);

  /* ======================================================
     FETCH — trabajadores + si ya tienen horario en esta temporada
  ====================================================== */

  const fetchTrabajadores = useCallback(async () => {
    if (!idEmpresa) return;
    setLoading(true);

    try {
      const { data: rawTrabajadores, error: errorTrabajadores } = await supabase
        .from('vista_trabajadores_detalles')
        .select('id_usuario, nombre_completo, email, foto')
        .eq('id_empresa', idEmpresa)
        .eq('activo', true);

      if (errorTrabajadores) throw errorTrabajadores;

      const { data: conHorario, error: errorHorario } =
        await filtrarPorTemporada(
          supabase
            .from('horario_trabajador')
            .select('id_usuario')
            .eq('id_empresa', idEmpresa)
        );

      if (errorHorario) throw errorHorario;

      const setConHorario = new Set(
        (conHorario || []).map((h: any) => String(h.id_usuario))
      );

      setTrabajadores(
        (rawTrabajadores || []).map((t) => {
          const tieneHorario = setConHorario.has(String(t.id_usuario));
          return {
            id_usuario: t.id_usuario,
            nombre_completo: t.nombre_completo || '',
            email: t.email || '',
            foto: t.foto ?? null,
            tiene_horario: tieneHorario,
            // Por defecto solo se marcan los que ya tenían esta jornada, para
            // que reciban el cambio. A los demás no se les asigna nada salvo
            // que se marquen a mano.
            seleccionado: tieneHorario,
          };
        })
      );
    } catch (err) {
      Sentry.captureException(err, { extra: { idEmpresa, idTemporada } });
      toast.error('Error cargando los trabajadores');
    } finally {
      setLoading(false);
    }
  }, [idEmpresa, idTemporada]);

  /* ======================================================
     TOGGLE LOCAL
  ====================================================== */

  const toggleTrabajador = (id_usuario: string, seleccionado: boolean) => {
    setTrabajadores((prev) =>
      prev.map((t) =>
        t.id_usuario === id_usuario ? { ...t, seleccionado } : t
      )
    );
  };

  const seleccionarTodos = (seleccionado: boolean) => {
    setTrabajadores((prev) => prev.map((t) => ({ ...t, seleccionado })));
  };

  /* ======================================================
     APLICAR — copia el horario de empresa a los marcados
  ====================================================== */

  const aplicarHorario = async () => {
    const seleccionados = trabajadores.filter((t) => t.seleccionado);
    if (seleccionados.length === 0) return { ok: true, aplicados: 0 };

    const idUsuarioSistema = localStorage.getItem('id_usuario') || null;
    setSaving(true);

    try {
      const { data: franjas, error: errorFranjas } = await filtrarPorTemporada(
        supabase
          .from('horario_empresa')
          .select('dia_semana, hora_inicio, hora_fin')
          .eq('id_empresa', idEmpresa)
          .eq('activo', true)
      );

      if (errorFranjas) throw errorFranjas;


      if (!franjas || franjas.length === 0) {
        toast.error('La empresa no tiene horarios configurados');
        return { ok: false, aplicados: 0 };
      }

      const ids = seleccionados.map((t) => t.id_usuario);

      const { error: deleteError } = await filtrarPorTemporada(
        supabase
          .from('horario_trabajador')
          .delete()
          .eq('id_empresa', idEmpresa)
          .in('id_usuario', ids)
      );

      if (deleteError) throw deleteError;


      const payload = ids.flatMap((id_usuario) =>
        (franjas as any[]).map((f) => ({
          id_usuario,
          id_empresa: idEmpresa,
          id_horario_temporada: idTemporada,
          dia_semana: f.dia_semana,
          hora_inicio: f.hora_inicio,
          hora_fin: f.hora_fin,
          activo: true,
          usuario_creacion: idUsuarioSistema,
        }))
      );


      const { data: insertadas, error: insertError } = await supabase
        .from('horario_trabajador')
        .insert(payload)
        .select('id_horario_trabajador');


      if (insertError) throw insertError;

      // Si RLS deja pasar el insert pero no devuelve filas, el horario no se
      // ha guardado y sin esto la pantalla diria que si.
      if (!insertadas || insertadas.length === 0) {
        throw new Error(
          'la base de datos no guardó ninguna franja (revisa permisos)'
        );
      }

      toast.success(
        seleccionados.length === 1
          ? 'Horario aplicado a 1 trabajador'
          : `Horario aplicado a ${seleccionados.length} trabajadores`
      );

      await fetchTrabajadores();
      return { ok: true, aplicados: seleccionados.length };
    } catch (err) {
      Sentry.captureException(err, { extra: { idEmpresa, idTemporada } });
      const detalle = (err as any)?.message ?? '';
      toast.error(
        detalle
          ? `No se pudo aplicar el horario: ${detalle}`
          : 'Error al aplicar el horario a los trabajadores'
      );
      return { ok: false, aplicados: 0 };
    } finally {
      setSaving(false);
    }
  };

  const sinHorario = trabajadores.filter((t) => !t.tiene_horario).length;

  return {
    trabajadores,
    loading,
    saving,
    sinHorario,
    fetchTrabajadores,
    toggleTrabajador,
    seleccionarTodos,
    aplicarHorario,
  };
}